import React, { useRef } from 'react';
import type { InvoiceBillingInput, BillingAddressForm } from '../hooks/billingAddressUtil';
import { useZipcodeSearch } from '../hooks/useZipcodeSearch';

import './BillingAddressInput.css';

interface BillingAddressInputProps {
  billingInput: InvoiceBillingInput;
  billingTypeOptions: { value: string; label: string }[];
  prefectureOptions: { value: string; label: string }[];
  onChange: (input: InvoiceBillingInput) => void;
}

/**
 * 請求書払い時の請求先情報入力コンポーネント
 *
 * - 請求先区分（ドロップダウン）— billing_types マスタ
 * - 請求先名（テキスト）
 * - 住所入力
 *
 * 顧客区分（法人・個人）による表示差は無い（2026-08-08 に法人の入力スキップを撤廃）。
 */
export const BillingAddressInput: React.FC<BillingAddressInputProps> = ({
  billingInput,
  billingTypeOptions,
  prefectureOptions,
  onChange,
}) => {
  const { billingTypeId, billingName, addressForm } = billingInput;

  const { search, isLoading: isAddressLoading, hasError: hasAddressError } =
    useZipcodeSearch(prefectureOptions);

  // 検索完了時に入力途中の他フィールドを巻き戻さないよう、最新の入力値を参照する
  const latestInputRef = useRef(billingInput);
  latestInputRef.current = billingInput;

  const handleZipCodeChange = (value: string) => {
    onChange({
      ...billingInput,
      addressForm: { ...addressForm, zipCode: value },
    });
    // 数字7桁になった時点で住所を自動反映（会員登録画面と同じ挙動）
    void search(value, (result) => {
      const latest = latestInputRef.current;
      onChange({
        ...latest,
        addressForm: { ...latest.addressForm, ...result },
      });
    });
  };

  const updateField = <K extends keyof InvoiceBillingInput>(
    key: K,
    value: InvoiceBillingInput[K],
  ) => {
    onChange({ ...billingInput, [key]: value });
  };

  const updateAddress = <K extends keyof BillingAddressForm>(
    key: K,
    value: BillingAddressForm[K],
  ) => {
    onChange({
      ...billingInput,
      addressForm: { ...addressForm, [key]: value },
    });
  };

  return (
    <div className="billing-input-section">
      <h3 className="billing-section-title">請求先情報</h3>

      {/* 請求先区分 */}
      <div className="billing-field">
        <label className="billing-label">
          請求先区分 <span className="required-mark">*</span>
        </label>
        <select
          className="billing-select"
          value={billingTypeId}
          onChange={(e) => updateField('billingTypeId', e.target.value)}
        >
          <option value="">選択してください</option>
          {billingTypeOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="billing-field">
        <label className="billing-label">
          請求先名 <span className="required-mark">*</span>
        </label>
        <input
          type="text"
          className="billing-text-input"
          value={billingName}
          onChange={(e) => updateField('billingName', e.target.value)}
          placeholder="例: 山田太郎"
        />
      </div>

      <div className="billing-field">
        <label className="billing-label">
          郵便番号 <span className="required-mark">*</span>
        </label>
        <input
          type="text"
          inputMode="numeric"
          className="billing-text-input billing-zip"
          value={addressForm.zipCode}
          onChange={(e) => handleZipCodeChange(e.target.value)}
          placeholder="例: 1000001"
          maxLength={7}
        />
        {isAddressLoading && (
          <p className="billing-zip-status">住所を検索しています…</p>
        )}
        {hasAddressError && (
          <p className="billing-zip-status billing-zip-error">
            住所を取得できませんでした。お手数ですが手入力してください。
          </p>
        )}
      </div>

      <div className="billing-field">
        <label className="billing-label">
          都道府県 <span className="required-mark">*</span>
        </label>
        <select
          className="billing-select"
          value={addressForm.addressPrefectureId}
          onChange={(e) => updateAddress('addressPrefectureId', e.target.value)}
        >
          <option value="">選択してください</option>
          {prefectureOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <div className="billing-field">
        <label className="billing-label">
          市区町村 <span className="required-mark">*</span>
        </label>
        <input
          type="text"
          className="billing-text-input"
          value={addressForm.addressCity}
          onChange={(e) => updateAddress('addressCity', e.target.value)}
          placeholder="例: 千代田区"
        />
      </div>

      <div className="billing-field">
        <label className="billing-label">
          町域番地 <span className="required-mark">*</span>
        </label>
        <input
          type="text"
          className="billing-text-input"
          value={addressForm.addressDetail}
          onChange={(e) => updateAddress('addressDetail', e.target.value)}
          placeholder="例: 丸の内1-1-1"
        />
      </div>

      <div className="billing-field">
        <label className="billing-label">建物名</label>
        <input
          type="text"
          className="billing-text-input"
          value={addressForm.addressBuilding}
          onChange={(e) => updateAddress('addressBuilding', e.target.value)}
          placeholder="例: ○○ビル3F"
        />
      </div>
    </div>
  );
};

// named export のみ（default export 禁止: coding-style.md）
